import { useEffect } from "react";

const BASE_URL = "https://rently.properties";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = "Rently Properties";

export const SEO = ({
  title = "Rently Properties - Kansas City Property Management",
  description = "Professional property management in Kansas City metro. Serving Overland Park, Leawood, Lenexa, Olathe and Johnson County. Expert tenant screening and rent collection.",
  canonical,
  type = "website",
  schema,
  ogImage = DEFAULT_OG_IMAGE,
  keywords = "property management, Kansas City, Overland Park, Leawood, Lenexa, Olathe, Johnson County, rental management, JOCO, Kansas",
  noindex = false,
}) => {
  const fullUrl = canonical ? `${BASE_URL}${canonical}` : BASE_URL;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper to set or create a meta tag
    const setMeta = (attr, attrValue, content) => {
      let el = document.querySelector(`meta[${attr}="${attrValue}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Helper to set or create a link tag
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    // Basic SEO Meta Tags
    setMeta("name", "description", description);
    setMeta("name", "keywords", keywords);
    setMeta("name", "author", SITE_NAME);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");

    // Canonical URL
    setLink("canonical", fullUrl);

    // Open Graph / Facebook
    setMeta("property", "og:type", type);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", fullUrl);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:locale", "en_US");
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:image:alt", title);

    // Twitter Card
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage);
    setMeta("name", "twitter:image:alt", title);

    // Geo Tags (for local SEO)
    setMeta("name", "geo.region", "US-KS");
    setMeta("name", "geo.placename", "Overland Park, Kansas");
    setMeta("name", "geo.position", "38.9822;-94.6708");
    setMeta("name", "ICBM", "38.9822, -94.6708");

    // Inject structured data (supports single schema or array of schemas)
    // First, remove all existing dynamic schema scripts
    document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());
    
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((s, i) => {
        const scriptEl = document.createElement("script");
        scriptEl.setAttribute("type", "application/ld+json");
        scriptEl.setAttribute("data-seo-schema", `true-${i}`);
        scriptEl.textContent = JSON.stringify(s);
        document.head.appendChild(scriptEl);
      });
    }

    // Cleanup on unmount
    return () => {
      document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());
    };
  }, [title, description, canonical, type, schema, ogImage, keywords, noindex, fullUrl]);

  // This component doesn't render anything visible
  return null;
};

// Enhanced LocalBusiness Schema
export const getLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  "name": "Rently Properties",
  "alternateName": "Rently Property Management",
  "description": "Professional property management services in Johnson County, Kansas. Serving Overland Park, Leawood, Lenexa, Olathe and the Kansas City metro area.",
  "url": "https://rently.properties",
  "telephone": "+1-913-795-1155",
  "email": "info@rently.properties",
  "logo": "https://rently.properties/rently-logo.png",
  "image": "https://rently.properties/og-image.png",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "6850 College Blvd",
    "addressLocality": "Overland Park",
    "addressRegion": "KS",
    "postalCode": "66211",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "38.9822",
    "longitude": "-94.6708"
  },
  "areaServed": [
    {
      "@type": "City",
      "name": "Overland Park",
      "containedInPlace": { "@type": "State", "name": "Kansas" }
    },
    {
      "@type": "City",
      "name": "Leawood",
      "containedInPlace": { "@type": "State", "name": "Kansas" }
    },
    {
      "@type": "City",
      "name": "Lenexa",
      "containedInPlace": { "@type": "State", "name": "Kansas" }
    },
    {
      "@type": "City",
      "name": "Olathe",
      "containedInPlace": { "@type": "State", "name": "Kansas" }
    },
    {
      "@type": "City",
      "name": "Shawnee",
      "containedInPlace": { "@type": "State", "name": "Kansas" }
    },
    {
      "@type": "City",
      "name": "Prairie Village",
      "containedInPlace": { "@type": "State", "name": "Kansas" }
    },
    {
      "@type": "City",
      "name": "Mission",
      "containedInPlace": { "@type": "State", "name": "Kansas" }
    },
    {
      "@type": "City",
      "name": "Merriam",
      "containedInPlace": { "@type": "State", "name": "Kansas" }
    }
  ],
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "08:00",
      "closes": "18:00"
    }
  ],
  "priceRange": "$$",
  "paymentAccepted": "ACH, Credit Card, Check",
  "currenciesAccepted": "USD",
  "knowsAbout": [
    "Property Management",
    "Tenant Screening",
    "Rent Collection",
    "Property Maintenance",
    "Real Estate Investment",
    "Kansas Landlord-Tenant Law"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Property Management Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Full-Service Property Management",
          "description": "Complete management including tenant placement, rent collection, maintenance coordination, and financial reporting."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Tenant Placement",
          "description": "Professional marketing, screening, and leasing services for rental properties in Johnson County."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Maintenance Coordination",
          "description": "24/7 maintenance request handling with a vetted network of licensed contractors."
        }
      }
    ]
  },
  "sameAs": []
});

// Service schema for process pages
export const getServiceSchema = (serviceName, serviceDescription) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": serviceName,
  "description": serviceDescription,
  "provider": {
    "@type": "RealEstateAgent",
    "name": "Rently Properties",
    "url": "https://rently.properties",
    "telephone": "+1-913-795-1155"
  },
  "areaServed": {
    "@type": "AdministrativeArea",
    "name": "Johnson County, Kansas"
  },
  "serviceType": "Property Management"
});

// BreadcrumbList schema
export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `https://rently.properties${item.path}`
  }))
});

// WebSite schema for sitelinks search box
export const getWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Rently Properties",
  "url": "https://rently.properties",
  "description": "Professional property management in Johnson County, Kansas",
  "publisher": {
    "@type": "Organization",
    "name": "Rently Properties",
    "logo": {
      "@type": "ImageObject",
      "url": "https://rently.properties/rently-logo.png"
    }
  }
});

export default SEO;
